import { SelectOption } from 'components/new-ui/composed/declarations/formSelect/DeclarationSelect';
import { DeclarationCountry } from './declaration-country';
import { DeclarationInternalType } from './declaration-internal-type';

export enum ICS2MessageTypes {
    F11 = 'F11',
    F50 = 'F50',
}

/**
 * An ENS filing is amended by the IE3Axx message mirroring the IE3Fxx it was lodged with, so the
 * amendment carries its own message type. It is kept apart from ICS2MessageTypes because a
 * declaration's own message type stays the filing type - only the amendment request uses these.
 */
export enum ICS2AmendmentMessageTypes {
    A11 = 'A11',
    A50 = 'A50',
}

const ics2AmendmentMessageTypes: Record<ICS2MessageTypes, ICS2AmendmentMessageTypes> = {
    [ICS2MessageTypes.F11]: ICS2AmendmentMessageTypes.A11,
    [ICS2MessageTypes.F50]: ICS2AmendmentMessageTypes.A50,
};

export const toIcs2AmendmentMessageType = (messageType: ICS2MessageTypes | undefined) =>
    messageType ? ics2AmendmentMessageTypes[messageType] : undefined;

export enum AISMessageTypes {
    H1 = 'H1',
    H7 = 'H7',
    I1 = 'I1',
}

export enum AESMessageTypes {
    B1 = 'B1',
    B2 = 'B2',
    B3 = 'B3',
    B4 = 'B4',
    C1 = 'C1',
    C2 = 'C2',
}

export enum ARRIVALMessageTypes {
    ARRIVAL = 'ARRIVAL',
}

// UK Specific declaration system
export enum CDSImportMessageTypes {
    H1 = 'H1',
    H2 = 'H2',
    H3 = 'H3',
    H4 = 'H4',
    H5 = 'H5',
}

// UK Specific declaration system
export enum CDSExportMessageTypes {
    B1 = 'B1',
    C1 = 'C1',
    B2 = 'B2',
    B4 = 'B4',
    C21E = 'C21E',
}

// Ireland and UK specific
// TODO: Remove these types in the future
export enum ENSMessageTypes {
    ENS = 'ENS',
}

// UK Specific declaration system
export enum GVMSMessageTypes {
    STANDARD_MOVEMENT_GMR = 'STANDARD_MOVEMENT_GMR',
    TIR_MOVEMENT_GMR = 'TIR_MOVEMENT_GMR',
    UNACCOMPANIED_ATA_CARNET_CONTAINER_MOVEMENT = 'UNACCOMPANIED_ATA_CARNET_CONTAINER_MOVEMENT',
    INDIRECT_EXPORT_DECLARATIONS_MOVEMENT_GMR = 'INDIRECT_EXPORT_DECLARATIONS_MOVEMENT_GMR',
    EMPTY_MOVEMENT_GMR = 'EMPTY_MOVEMENT_GMR',
    ORAL_OR_BY_CONDUCT_DECLARATION = 'ORAL_OR_BY_CONDUCT_DECLARATION',
    EXEMPTION_MOVEMENT_RECORD = 'EXEMPTION_MOVEMENT_RECORD',
    UKC = 'UKC',
}

export enum TSDMessageTypes {
    G4 = 'G_4',
    G4G3 = 'G_4_G_3',
}

// EU-wide and internation - non-country specific
export enum NCTSMessageTypes {
    D1 = 'D1',
    D2 = 'D2',
    D3 = 'D3',
    D4 = 'D4',
}

// Exit Summary Declaration
// not system-specific
export enum ETDMessageTypes {
    D3 = 'D3',
}

export enum DeclarationTypes {
    CDS = 'CDS',
    ENS = 'ENS',
    NCTS = 'NCTS',
    AIS = 'AIS',
    ETD = 'ETD',
    AES = 'AES',
    ICS2 = 'ICS2',
}
export const MessageTypesMerge = {
    ...AISMessageTypes,
    ...AESMessageTypes,
    ...ARRIVALMessageTypes,
    ...CDSImportMessageTypes,
    ...CDSExportMessageTypes,
    ...ENSMessageTypes,
    ...GVMSMessageTypes,
    ...TSDMessageTypes,
    ...NCTSMessageTypes,
    ...ETDMessageTypes,
    ...ICS2MessageTypes,
};

export type MessageTypes =
    | AISMessageTypes
    | AESMessageTypes
    | ARRIVALMessageTypes
    | CDSImportMessageTypes
    | CDSExportMessageTypes
    | ENSMessageTypes
    | GVMSMessageTypes
    | TSDMessageTypes
    | NCTSMessageTypes
    | ETDMessageTypes
    | ICS2MessageTypes;

const irelandMessageTypes: Record<string, MessageTypes> = {
    ...AISMessageTypes,
    ...AESMessageTypes,
    ...ARRIVALMessageTypes,
    ...ENSMessageTypes,
    ...TSDMessageTypes,
    ...NCTSMessageTypes,
    ...ETDMessageTypes,
};

const ukMessageTypes: Record<string, MessageTypes> = {
    ...CDSImportMessageTypes,
    ...CDSExportMessageTypes,
    ...GVMSMessageTypes,
    ...ENSMessageTypes,
    ...NCTSMessageTypes,
    ...ETDMessageTypes,
};

const euMessageTypes: Record<string, ICS2MessageTypes> = {
    ...ICS2MessageTypes,
};

const internalTypeMessageMap: Partial<Record<DeclarationInternalType, Record<string, string>>> = {
    [DeclarationInternalType.IMPORT]: { ...AISMessageTypes, ...CDSImportMessageTypes },
    [DeclarationInternalType.IMPORT_NEW]: { ...AISMessageTypes },
    [DeclarationInternalType.EXPORT]: { ...AESMessageTypes, ...CDSExportMessageTypes },
    [DeclarationInternalType.ENS]: { ...ICS2MessageTypes },
    [DeclarationInternalType.ETD]: { ...ETDMessageTypes },
    [DeclarationInternalType.ARRIVAL]: { ...ARRIVALMessageTypes },
    [DeclarationInternalType.TEMPORARY]: { ...TSDMessageTypes },
    [DeclarationInternalType.GVMS]: { ...GVMSMessageTypes },
    [DeclarationInternalType.NCTS]: { ...NCTSMessageTypes },
    [DeclarationInternalType.PBN]: { ...ENSMessageTypes },
};

const mapToOptions = (map: Record<string, string>): SelectOption[] =>
    Object.values(map).map((code) => ({
        id: code,
        value: code,
    }));

const filterByInternalTypes = (
    base: Record<string, string>,
    declarationInternalTypes: DeclarationInternalType[]
): Record<string, string> => {
    const allowedValues = new Set(
        declarationInternalTypes.flatMap((t) => Object.values(internalTypeMessageMap[t] ?? {}))
    );
    return Object.fromEntries(Object.entries(base).filter(([, v]) => allowedValues.has(v)));
};

export const getMessageTypeSelectOptions = (
    country?: DeclarationCountry,
    declarationInternalTypes?: DeclarationInternalType[]
): SelectOption[] => {
    let base: Record<string, string>;

    if (country === DeclarationCountry.IRELAND) {
        base = irelandMessageTypes;
    } else if (country === DeclarationCountry.UK) {
        base = ukMessageTypes;
    } else if (country === DeclarationCountry.EU) {
        base = euMessageTypes;
    } else {
        base = MessageTypesMerge;
    }

    if (declarationInternalTypes && declarationInternalTypes.length > 0) {
        base = filterByInternalTypes(base, declarationInternalTypes);
    }

    return mapToOptions(base);
};
